/**
 * Controlled form for the Auth Letter Upload modal.
 * All state lives in the caller; this component only renders and
 * forwards user input via the `onChange` callback.
 */
import {
  DropdownInput,
  InputField,
  MultiTagTextEntry,
  SearchableAsyncSelect,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo } from "react";
import { AuthLetterUploadFormState } from "../../../data-model/candidAuthorizationModel";
import { useDebouncedCandidPayers } from "./hooks/useDebouncedCandidPayers";

const BASE_CLASS_NAME = "candid-auth-form-container";
// Grid tracks are minmax(0, 1fr), so a long placeholder shrinks its field
// instead of widening the column past the modal body.
const ROW_CLASS_NAME = "grid w-full grid-cols-2 items-baseline gap-[0.875rem]";
const ROW_CHILD_CLASS_NAME = "min-w-0";

/** Unit options shared by the Units dropdown. */
const UNIT_OPTIONS = [
  { label: "VISIT", value: "VISIT" },
  { label: "UNIT", value: "UNIT" },
];

/** Controlled props for {@link AuthLetterUploadForm}. */
interface AuthLetterUploadFormProps {
  /** Current form field values owned by the parent modal. */
  value: AuthLetterUploadFormState;
  /**
   * Updates a single form field.
   *
   * @param field - Key of {@link AuthLetterUploadFormState} to change
   * @param value - Next value for that field
   */
  onChange: <K extends keyof AuthLetterUploadFormState>(
    field: K,
    value: AuthLetterUploadFormState[K],
  ) => void;
}

/**
 * Renders the controlled auth letter upload fields and forwards edits.
 * Payer name is an abortable async Candid directory search whose dropdown
 * mounts into the enclosing Modal's portal root, so it escapes the scrolling
 * form body instead of being clipped by it.
 * Most fields are marked required; Notes and NPI Billing Number are optional.
 * The authorization window uses Start Date and Expiration Date (`endDate`).
 *
 * @param props.value - Current form snapshot from the parent
 * @param props.onChange - Field updater invoked on every user edit
 * @returns JSX for the auth letter upload form
 */
const AuthLetterUploadForm = ({
  value,
  onChange,
}: AuthLetterUploadFormProps) => {
  const { searchPayers } = useDebouncedCandidPayers();
  const modalPortalTarget = useModalPortalTarget();
  const payerInitialOptions = useMemo(
    () =>
      value.payerId && value.payerName
        ? [{ value: value.payerId, label: value.payerName }]
        : [],
    [value.payerId, value.payerName],
  );
  return (
    <div className={`${BASE_CLASS_NAME} flex min-h-0 flex-col gap-[0.875rem]`}>
      <div className={ROW_CLASS_NAME}>
        <div className={ROW_CHILD_CLASS_NAME}>
          <SearchableAsyncSelect
            id="payer-name"
            label="Payer Name"
            placeholder="Search for a payer name"
            debounceMs={500}
            value={value.payerId}
            initialOptions={payerInitialOptions}
            onSearch={searchPayers}
            usePortal
            targetRef={modalPortalTarget}
            onChange={(option) => {
              onChange("payerName", option?.label ?? "");
              onChange("payerId", option?.value ?? "");
            }}
            required
          />
        </div>
        <div className={ROW_CHILD_CLASS_NAME}>
          <InputField
            label="Authorization Number"
            id="authorization-number"
            placeholder="Enter authorization number"
            size="md"
            fullWidth
            value={value.authorizationNumber}
            onChange={(e) => onChange("authorizationNumber", e.target.value)}
            required
          />
        </div>
      </div>
      <div className={ROW_CLASS_NAME}>
        <div className={ROW_CHILD_CLASS_NAME}>
          <InputField
            label="Start Date"
            id="start-date"
            placeholder="Enter start date"
            size="md"
            fullWidth
            type="date"
            dateFormat="mm/dd/yyyy"
            value={value.startDate}
            onDateChange={(formattedDate) =>
              onChange("startDate", formattedDate)
            }
            required
          />
        </div>
        <div className={ROW_CHILD_CLASS_NAME}>
          <InputField
            label="Expiration Date"
            id="end-date"
            placeholder="Enter expiration date"
            size="md"
            fullWidth
            type="date"
            dateFormat="mm/dd/yyyy"
            value={value.endDate}
            onDateChange={(formattedDate) => onChange("endDate", formattedDate)}
            required
          />
        </div>
      </div>
      <div className={ROW_CLASS_NAME}>
        <div className={ROW_CHILD_CLASS_NAME}>
          <MultiTagTextEntry
            label="Service Codes"
            id="service-codes"
            placeholder="Enter service codes separated by commas and hit enter"
            size="md"
            fullWidth
            value={value.serviceCodes}
            onChange={(tags) => onChange("serviceCodes", tags)}
            required
          />
        </div>
        <div className={ROW_CHILD_CLASS_NAME}>
          <MultiTagTextEntry
            label="Diagnosis Codes"
            id="dx-codes"
            placeholder="Enter dx codes separated by commas and hit enter"
            size="md"
            fullWidth
            value={value.dx_codes}
            onChange={(tags) => onChange("dx_codes", tags)}
            required
          />
        </div>
      </div>
      <div className={ROW_CLASS_NAME}>
        <div className={ROW_CHILD_CLASS_NAME}>
          <InputField
            label="Quantity"
            id="quantity"
            placeholder="Enter quantity"
            size="md"
            fullWidth
            value={value.quantity}
            onChange={(e) => onChange("quantity", e.target.value)}
            required
          />
        </div>
        <div className={ROW_CHILD_CLASS_NAME}>
          <DropdownInput
            label="Units"
            id="units"
            placeholder="Select units"
            className="w-full"
            options={UNIT_OPTIONS}
            size="medium"
            value={value.units}
            onChange={(nextValue) => onChange("units", nextValue)}
            required
          />
        </div>
      </div>
      <div className={ROW_CLASS_NAME}>
        <div className={ROW_CHILD_CLASS_NAME}>
          <InputField
            label="Notes"
            id="notes"
            placeholder="Enter notes"
            size="md"
            fullWidth
            value={value.notes}
            onChange={(e) => onChange("notes", e.target.value)}
          />
        </div>
        <div className={ROW_CHILD_CLASS_NAME}>
          <InputField
            label="NPI Billing Number"
            id="npi-billing-number"
            placeholder="Enter NPI Billing Number"
            size="md"
            fullWidth
            required={false}
            value={value.npiBillingNumber ?? ""}
            onChange={(e) => onChange("npiBillingNumber", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

export default AuthLetterUploadForm;
